<script setup>
import { FilterMatchMode } from '@primevue/core/api';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref, computed, reactive, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { watchDebounced } from '@vueuse/core';
import axios from 'axios';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    clients: Object,
    filters: Object,
});

const toast = useToast();
const dt = ref();
const deleteClientDialog = ref(false);
const deleteClientsDialog = ref(false);
const impersonateDialog = ref(false);
const clientToDelete = ref({});
const selectedClients = ref();

function toggleAllActive() {
    const activeClients = props.clients?.data?.filter(client => !isClientDeleted(client)) || [];
    const allActive = activeClients.length > 0 && activeClients.every(client => selectedClients.value?.includes(client));
    
    if (allActive) {
        selectedClients.value = selectedClients.value?.filter(selected => isClientDeleted(selected)) || [];
    } else {
        const deletedSelected = selectedClients.value?.filter(selected => isClientDeleted(selected)) || [];
        selectedClients.value = [...deletedSelected, ...activeClients];
    }
}
const clientToImpersonate = ref({});
// Live impersonation state, fetched at click time so the warning reflects the
// current (tab-shared) session, not a stale page-load value.
const activeImpersonation = ref({ active: false, name: null });
const checkingImpersonation = ref(false);

// Filters reactive object
const searchFilters = reactive({
    search: props.filters?.search || '',
    full: props.filters?.full || null,
});

// Sort state
const sortField = ref('clients.name');
const sortOrder = ref(1);
const lastSortField = ref('clients.name');

// PrimeVue DataTable filters
const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
});

const fullTypeOptions = ref([
    { label: 'Full', value: true },
    { label: 'Limitado', value: false },
]);

// Watch for filter changes with debounce
watchDebounced(() => ({ ...searchFilters }), (n, o) => {
    fetchClients();
}, {
    debounce: 400,
    deep: true,
});

// Fetch clients with filters
const fetchClients = (page = null) => {
    const params = {
        filter: {
            search: searchFilters.search,
            full: searchFilters.full?.value,
        },
    };

    if (page) {
        params.page = page;
    }

    // Add sort parameter (Spatie QueryBuilder format: '-field' for descending)
    if (sortField.value) {
        params.sort = sortOrder.value === -1 ? `-${sortField.value}` : sortField.value;
    }

    router.get(route('clients.index'), params, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading($t('Loading clients...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
};

// Handle pagination
const onPage = (event) => {
    const page = event.page + 1;
    fetchClients(page);
};

// Handle sorting - first click is always descending
const onSort = (event) => {
    // Map old field names to new ones for backward compatibility
    const fieldMapping = {
        'name': 'clients.name',
        'consultant': 'consultant_name',
        'latest_login': 'latest_login_at'
    };

    const mappedField = fieldMapping[event.sortField] || event.sortField;

    // If it's a new column, start with descending (-1)
    if (mappedField !== lastSortField.value) {
        sortField.value = mappedField;
        sortOrder.value = -1;
        lastSortField.value = mappedField;
    } else {
        // If same column, toggle the order
        sortField.value = mappedField;
        sortOrder.value = event.sortOrder;
    }
    fetchClients();
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};


// Navigation functions
function addClient() {
    router.visit(route('clients.create'), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
}

function editClient(client) {
    router.visit(route('clients.edit', client.id), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
}

function confirmDeleteClient(client) {
    clientToDelete.value = client;
    deleteClientDialog.value = true;
}

function deleteClient() {
    router.delete(route('clients.destroy', clientToDelete.value.id), {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading($t('Deleting client...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            deleteClientDialog.value = false;
            clientToDelete.value = {};
            toast.add({
                severity: 'success',
                summary: $t('Successful'),
                detail: $t('Client deleted successfully'),
                life: 3000
            });
        },
        onError: () => {
            hideLoading();
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to delete client'),
                life: 3000
            });
        }
    });
}

function confirmDeleteSelected() {
    deleteClientsDialog.value = true;
}

function deleteSelectedClients() {
    // Implement batch delete logic here
    const clientIds = selectedClients.value.map(c => c.id);

    router.delete(route('clients.bulk-destroy'), {
        data: { ids: clientIds }
    }, {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading($t('Deleting clients...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            deleteClientsDialog.value = false;
            selectedClients.value = null;
            toast.add({
                severity: 'success',
                summary: $t('Successful'),
                detail: $t('Clients deleted successfully'),
                life: 3000
            });
        },
        onError: () => {
            hideLoading();
        }
    });
}

async function confirmImpersonate(client) {
    clientToImpersonate.value = client;
    activeImpersonation.value = { active: false, name: null };
    impersonateDialog.value = true;

    // Check the live session state so we can warn if an impersonation is already
    // active in another tab (starting a new one would break that tab).
    checkingImpersonation.value = true;
    try {
        const { data } = await axios.get(route('impersonate.status'));
        activeImpersonation.value = data;
    } catch (e) {
        // If the check fails, fall back to the plain confirmation (no warning).
        activeImpersonation.value = { active: false, name: null };
    } finally {
        checkingImpersonation.value = false;
    }
}

function impersonateClient() {
    // Open the impersonated client in a NEW tab. The admin keeps working in the
    // current tab (the `web` guard is never logged out by the impersonate flow).
    window.open(
        route('impersonate', [clientToImpersonate.value.id, 'client', 'client.dashboard']),
        '_blank'
    );
    impersonateDialog.value = false;
}

function exportCSV() {
    dt.value.exportCSV();
}

// Format date function
function formatDate(dateString) {
    if (!dateString) return $t('Never');
    return new Date(dateString).toLocaleString();
}

// Get consultant name
function getConsultantName(client) {
    return client.consultant?.name || $t('None');
}

// Check if client is full
function isFullClient(client) {
    return client.full === true || client.full === 1;
}

function isClientDeleted(client) {
    return client.deleted_at !== null;
}

onMounted(() => {
    // Initialize any tooltips if needed
    if (typeof tippy !== 'undefined') {
        setTimeout(() => {
            tippy('[data-tippy-content]');
        }, 100);
    }
});

defineOptions({
    layout: AdminMainLayout,
});
</script>

<template>
    <div>
        <!-- Search Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex flex-col gap-4">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-3">
                            <i class="pi pi-search text-2xl text-primary-500"></i>
                            <div>
                                <h2 class="text-xl font-bold">{{ $t('Search clients') }}</h2>
<!--                                <span class="text-sm text-surface-500">{{ $t('Choose filters to start') }}</span>-->
                            </div>
                        </div>
<!--                        <Button-->
<!--                            :label="$t('Add client')"-->
<!--                            icon="pi pi-plus"-->
<!--                            severity="primary"-->
<!--                            @click="addClient"-->
<!--                            class="hidden md:flex"-->
<!--                        />-->
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div class="md:col-span-2">
                            <label class="block text-sm font-medium mb-2">{{ $t('Find by name or email:') }}</label>
                            <IconField>
                                <InputIcon>
                                    <i class="pi pi-search" />
                                </InputIcon>
                                <InputText
                                    v-model="searchFilters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full"
                                />
                           </IconField>
                       </div>

                       <div>
                           <label class="block text-sm font-medium mb-2">{{ $t('Filter by type') }}</label>
                           <Select
                               v-model="searchFilters.full"
                               :options="fullTypeOptions"
                               optionLabel="label"
                               :placeholder="$t('Select...')"
                               class="w-full"
                               showClear
                           />
                       </div>
                   </div>
                </div>
            </template>
        </Card>

        <!-- Results Card -->
        <Card>
            <template #header>
                <div class="flex justify-between items-center p-6 pb-0">
                    <h3 class="text-lg font-semibold">
                        {{ $t('Showing results:') }} {{ clients?.total || 0 }} {{ $t('clients found') }}
                    </h3>
                    <div class="flex gap-2">
                        <Button
                            :label="$t('Add client')"
                            icon="pi pi-plus"
                            severity="primary"
                            @click="addClient"
                            class="md:hidden"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <Toolbar class="mb-4">
                    <template #start>
                        <Button
                            label="Delete"
                            icon="pi pi-trash"
                            severity="danger"
                            @click="confirmDeleteSelected"
                            :disabled="!selectedClients || !selectedClients.length"
                        />
                    </template>

                    <template #end>
                        <Button
                            label="Export"
                            icon="pi pi-upload"
                            severity="secondary"
                            @click="exportCSV($event)"
                        />
                    </template>
                </Toolbar>

                <DataTable
                    ref="dt"
                    v-model:selection="selectedClients"
                    :value="clients?.data || []"
                    dataKey="id"
                    :paginator="true"
                    :rows="15"
                    :totalRecords="clients?.total"
                    :lazy="true"
                    @page="onPage"
                    @sort="onSort"
                    :sortField="sortField"
                    :sortOrder="sortOrder"
                    :filters="filters"
                    responsiveLayout="scroll"
                    :loading="false"
                    :paginatorTemplate="paginatorTemplate.layout"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    class="p-datatable-sm"
                >
                    <template #header>
                        <div class="flex flex-wrap gap-2 items-center justify-between">
                            <h4 class="m-0">{{ $t('Clients list') }}</h4>
<!--                            <IconField>-->
<!--                                <InputIcon>-->
<!--                                    <i class="pi pi-search" />-->
<!--                                </InputIcon>-->
<!--                                <InputText-->
<!--                                    v-model="filters['global'].value"-->
<!--                                    :placeholder="$t('Quick search...')"-->
<!--                                />-->
<!--                            </IconField>-->
                        </div>
                    </template>

                    <template #empty>
                        <div class="text-center py-8">
                            <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                            <p class="text-surface-500">{{ $t('No clients found') }}</p>
                        </div>
                    </template>

                    <Column style="width: 3rem" :exportable="false">
                        <template #header>
                            <Checkbox @click="toggleAllActive" />
                        </template>
                        <template #body="slotProps">
                            <Checkbox
                                :value="slotProps.data"
                                v-model="selectedClients"
                                :disabled="isClientDeleted(slotProps.data)"
                                :class="{ 'opacity-30': isClientDeleted(slotProps.data) }"
                            />
                        </template>
                    </Column>

                    <Column field="name" :header="$t('Name')" sortable style="min-width: 16rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2" :class="{ 'opacity-50': isClientDeleted(slotProps.data) }">
                                <div>
                                    <div class="flex items-center gap-2">
                                        <p class="font-medium" :class="{ 'line-through': isClientDeleted(slotProps.data) }">
                                            {{ ((slotProps.data.first_name ?? '') + ' ' + (slotProps.data.last_name ?? '')).trim() || slotProps.data.name }}
                                        </p>
                                        <Tag v-if="isClientDeleted(slotProps.data)" value="Deleted" severity="danger" class="text-xs" />
                                    </div>
                                    <p class="text-sm text-surface-500" :class="{ 'line-through': isClientDeleted(slotProps.data) }">
                                        {{ slotProps.data.email }}
                                    </p>
                                </div>
                                <Tag
                                    v-if="isFullClient(slotProps.data) && !isClientDeleted(slotProps.data)"
                                    value="FULL"
                                    severity="warn"
                                    icon="pi pi-star-fill"
                                />
                            </div>
                        </template>
                    </Column>

                    <Column field="consultant_name" :header="$t('Consultant')" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            <span :class="{ 'opacity-50 line-through': isClientDeleted(slotProps.data) }">
                                {{ getConsultantName(slotProps.data) }}
                            </span>
                        </template>
                    </Column>

                    <Column field="latest_login_at" :header="$t('Last Seen')" sortable style="min-width: 12rem">
                        <template #body="slotProps">
                            <span class="text-sm" :class="{ 'opacity-50 line-through': isClientDeleted(slotProps.data) }">
                                {{ formatDate(slotProps.data.latest_login?.created_at) }}
                            </span>
                        </template>
                    </Column>

                    <Column field="client_info" :header="$t('Client Information')" style="min-width: 14rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-3 flex-wrap" :class="{ 'opacity-50': isClientDeleted(slotProps.data) }">
                                <div
                                    v-for="info in [
                                        { icon: 'pi pi-lock', label: $t('Logins'), value: slotProps.data.logins_count },
                                        { icon: 'pi pi-shopping-cart', label: $t('Orders'), value: slotProps.data.orders_count },
                                        { icon: 'pi pi-eye', label: $t('Views'), value: slotProps.data.views_count },
                                        { icon: 'pi pi-folder', label: $t('Projects'), value: slotProps.data.projects_count },
                                        { icon: 'pi pi-heart', label: $t('Favorites'), value: slotProps.data.favorites_count },
                                        { icon: 'pi pi-flag', label: $t('Interests'), value: slotProps.data.sites_in_projects_count }
                                    ]"
                                    :key="info.label"
                                    class="relative inline-flex items-center"
                                >
                                    <i
                                        :class="[
                                            info.icon,
                                            'text-base transition-colors duration-200',
                                            isClientDeleted(slotProps.data) ? 'text-surface-300' : (info.value > 0 ? 'text-surface-700' : 'text-surface-300')
                                        ]"
                                        v-tooltip.top="`${info.label}: ${info.value || 0}`"
                                    ></i>
                                </div>
                            </div>
                        </template>
                    </Column>

                    <Column field="introduced_by" :header="$t('Introduced by')" sortable style="min-width: 6rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2" :class="{ 'opacity-50': isClientDeleted(slotProps.data) }">
                                <span :class="{ 'line-through': isClientDeleted(slotProps.data) }">
                                    {{ slotProps.data.introduced_by || '' }}
                                </span>
                            </div>
                        </template>
                    </Column>

                    <Column :header="$t('Actions')" :exportable="false" style="min-width: 10rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                <Button
                                    icon="pi pi-pencil"
                                    severity="info"
                                    text
                                    rounded
                                    v-tooltip.top="isClientDeleted(slotProps.data) ? $t('Client is deleted') : $t('Edit')"
                                    @click="editClient(slotProps.data)"
                                    :disabled="isClientDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isClientDeleted(slotProps.data) }"
                                />
                                <Button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    text
                                    rounded
                                    v-tooltip.top="isClientDeleted(slotProps.data) ? $t('Already deleted') : $t('Delete')"
                                    @click="confirmDeleteClient(slotProps.data)"
                                    :disabled="isClientDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isClientDeleted(slotProps.data) }"
                                />
                                <Button
                                    icon="pi pi-sign-in"
                                    severity="secondary"
                                    text
                                    rounded
                                    v-tooltip.top="isClientDeleted(slotProps.data) ? $t('Client is deleted') : $t('Impersonate')"
                                    @click="confirmImpersonate(slotProps.data)"
                                    :disabled="isClientDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isClientDeleted(slotProps.data) }"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </template>
        </Card>

        <!-- Delete Client Dialog -->
        <Dialog
            v-model:visible="deleteClientDialog"
            :style="{ width: '450px' }"
            :header="$t('Confirm')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span v-if="clientToDelete">
                    {{ $t('Are you sure you want to delete') }} <b>{{ clientToDelete.name || ((clientToDelete.first_name || '') + ' ' + (clientToDelete.last_name || '')) }}</b>?
                </span>
            </div>
            <template #footer>
                <Button
                    :label="$t('No')"
                    icon="pi pi-times"
                    text
                    @click="deleteClientDialog = false"
                />
                <Button
                    :label="$t('Yes')"
                    icon="pi pi-check"
                    severity="danger"
                    @click="deleteClient"
                />
            </template>
        </Dialog>

        <!-- Delete Multiple Clients Dialog -->
        <Dialog
            v-model:visible="deleteClientsDialog"
            :style="{ width: '450px' }"
            :header="$t('Confirm')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span>{{ $t('Are you sure you want to delete the selected clients?') }}</span>
            </div>
            <template #footer>
                <Button
                    :label="$t('No')"
                    icon="pi pi-times"
                    text
                    @click="deleteClientsDialog = false"
                />
                <Button
                    :label="$t('Yes')"
                    icon="pi pi-check"
                    severity="danger"
                    @click="deleteSelectedClients"
                />
            </template>
        </Dialog>

        <!-- Impersonate Dialog -->
        <Dialog
            v-model:visible="impersonateDialog"
            :style="{ width: '450px' }"
            :header="$t('Impersonate Client')"
            :modal="true"
        >
            <div class="flex flex-col gap-4">
                <div class="flex items-center gap-4">
                    <i class="pi pi-sign-in text-3xl text-primary-500" />
                    <span v-if="clientToImpersonate">
                        {{ $t('Do you want to impersonate') }} <b>{{ clientToImpersonate.name || ((clientToImpersonate.first_name || '') + ' ' + (clientToImpersonate.last_name || '')) }}</b>?
                    </span>
                </div>
                <Message
                    v-if="activeImpersonation.active"
                    severity="warn"
                    :closable="false"
                >
                    {{ $t('There is already an active impersonation of') }}
                    <b>{{ activeImpersonation.name }}</b>.
                    {{ $t('Continuing will end that session. Do you want to proceed?') }}
                </Message>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="impersonateDialog = false"
                />
                <Button
                    :label="$t('Impersonate')"
                    icon="pi pi-sign-in"
                    severity="secondary"
                    :loading="checkingImpersonation"
                    @click="impersonateClient"
                />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>

:deep(.p-datatable-header) {
  border-bottom: none !important;
  box-shadow: none !important;
}

:deep(.p-datatable thead > tr > th) {
  height: 44.2px !important;
  vertical-align: middle !important;
}

:deep(.p-select) {
    height: 41px;
}

:deep(.p-select .p-select-label) {
    padding: 8px 12px;
    line-height: 1.5;
}
</style>